import { env } from '@/config/env';
import { axiosClient } from '@/libs/axios-client';

/**
 * Asks the backend whether a password reset link is still usable.
 * @param {string} token - The token ID for password reset.
 * @returns {Promise<string>} Resolves with the token when valid, rejects otherwise.
 */
export const passwordReset = async (token) => {
  try {
    const response = await axiosClient.get(
      `/auth/password-reset/${token}/validate`
    );

    if (!response?.data?.valid) {
      throw new Error('Password reset token is invalid or expired');
    }

    return token;
  } catch (err) {
    console.error(err);
    throw err;
  }
};

/**
 * Resolves the new password for the user.
 * @param {string} newPassword - The new password to set for the user.
 * @param {string} token - The token for password reset.
 * @returns {Promise<string>} A promise that resolves with the new password.
 */
export const resetPassword = async (newPassword, token) => {
  try {
    const response = await axiosClient.post(`/auth/password-reset/${token}`, {
      password: newPassword,
      confirmPassword: newPassword,
    });
    return response.data;
  } catch (error) {
    console.error('Error sending reset password request:', error);
    throw error; // Propagate the error for handling in the component
  }
};

/**
 * Sends a request to reset the password for the given email.
 * @param {string} email - The email address for which to send the password reset request.
 * @returns {Promise<Object>} A promise that resolves with the response from the server.
 */
export const forgotPassword = async (email) => {
  try {
    const response = await axiosClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    console.error('Error sending forgot password request:', error);
    throw error; // Propagate the error for handling in the component
  }
};

/**
 * Fetches the current user check status from the server.
 * @returns {Promise<Object | null>} A promise that resolves with the user check status data or null if unauthorized.
 */
export const fetchUserCheckStatus = async () => {
  try {
    const response = await axiosClient.get('/auth/user-check');
    return response.data;
  } catch (error) {
    if (error.response?.status === 401) {
      console.warn('Unauthorized access - returning null');
      return null; // Return null instead of throwing
    }
    console.error('Error fetching user check status:', error);
    throw error; // Propagate other errors
  }
};

/**
 * Completes a sign-in that asked for an emailed code.
 *
 * @param {Object} payload - Verification payload.
 * @param {string} payload.mfaToken - Challenge id returned by the login call.
 * @param {string} payload.code - The six-digit code from the email.
 * @param {boolean} [payload.rememberDevice] - Skip the code on this browser
 *   for the next 30 days.
 * @returns {Promise<Object>} The same user payload a plain login returns.
 */
export const verifyMfa = async ({ mfaToken, code, rememberDevice }) => {
  const response = await axiosClient.post('/auth/mfa/verify', {
    mfaToken,
    code,
    rememberDevice,
  });

  return response.data;
};

/**
 * Asks for a fresh code for an open challenge.
 *
 * @param {string} mfaToken - Challenge id returned by the login call.
 * @returns {Promise<Object>} Server message and the masked address.
 */
export const resendMfa = async (mfaToken) => {
  const response = await axiosClient.post('/auth/mfa/resend', { mfaToken });

  return response.data;
};

/**
 * First sign-in step: what to offer for the address that was typed.
 *
 * The reply is deliberately the same for an address the server does not know
 * as for an ordinary account, so nothing here tells the caller whether an
 * account exists.
 *
 * @param {string} email - The address from the first step.
 * @returns {Promise<{password: boolean, providers: Array<string>}>} Methods.
 */
export const getLoginMethods = async (email) => {
  const response = await axiosClient.post('/auth/login/methods', { email });

  return response.data;
};

/**
 * Which single sign-on providers the server has credentials for.
 *
 * @returns {Promise<Object>} `{ microsoft: boolean, google: boolean }`.
 */
export const getSsoStatus = async () => {
  const response = await axiosClient.get('/auth/sso/status');

  return response.data;
};

/**
 * Where to send the browser to start a single sign-on flow.
 *
 * A full page navigation, not an XHR: the provider answers with its own login
 * page and finishes by redirecting back to the API, which needs to set the
 * session cookie itself.
 *
 * @param {string} provider - `microsoft` or `google`.
 * @returns {string} Absolute URL.
 */
export const ssoStartUrl = (provider) =>
  `${env.apiURL}/auth/sso/${provider}/start`;

/**
 * Whether the API is awaiting a code for this browser.
 *
 * The single sign-on callback answers with a redirect, so it cannot hand the
 * challenge back the way the password step does; it parks it in the session
 * and the login screen asks here after the bounce.
 *
 * @returns {Promise<{pending: boolean, maskedEmail: string|null}>}
 */
export const getMfaPending = async () => {
  const response = await axiosClient.get('/auth/mfa/pending');

  return response.data;
};

const AuthService = {
  passwordReset,
  resetPassword,
  forgotPassword,
  fetchUserCheckStatus,
  verifyMfa,
  resendMfa,
  getMfaPending,
  getLoginMethods,
  getSsoStatus,
  ssoStartUrl,
};

export default AuthService;
